import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp,
  ShoppingBag,
  DollarSign,
  Calendar,
  Filter,
  Download,
  Printer,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Store,
  CreditCard,
  Search,
  BarChart2,
  MapPin,
  ChevronRight,
} from 'lucide-react';
import { CustomerOrder } from '../../types';
import { fetchAllOrders, subscribeToOrdersRealtime } from '../../utils/supabaseOrders';
import { matchesOutlet } from '../../data/adminAccounts';

type PeriodFilter = 'today' | '7days' | '30days' | 'this_month' | 'all';

interface OutletPeriodMetrics {
  sudirmanSales: number;
  sudirmanCount: number;
  kelakapSales: number;
  kelakapCount: number;
  totalSales: number;
  totalCount: number;
}

export const SalesReportManager: React.FC = () => {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [selectedOutlet, setSelectedOutlet] = useState<string>('ALL');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState<string | null>(null);

  // Load orders from Supabase Production
  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAllOrders('ALL');
      setOrders(data);
    } catch (err) {
      console.warn('Failed to load sales orders:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();

    const unsubscribe = subscribeToOrdersRealtime((updatedList) => {
      setOrders(updatedList);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Helper date calculations
  const calculateOutletMetricsForPeriod = (
    orderList: CustomerOrder[],
    filterFn: (orderDate: Date, now: Date) => boolean
  ): OutletPeriodMetrics => {
    const now = new Date();
    const filtered = orderList.filter((o) => filterFn(new Date(o.createdAt), now));
    const paid = filtered.filter((o) => o.paymentStatus === 'PAID');

    const sudirmanPaid = paid.filter((o) => matchesOutlet(o.outletId, 'outlet-sudirman'));
    const sudirmanSales = sudirmanPaid.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    const sudirmanCount = sudirmanPaid.length;

    const kelakapPaid = paid.filter((o) => matchesOutlet(o.outletId, 'outlet-kelakap'));
    const kelakapSales = kelakapPaid.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    const kelakapCount = kelakapPaid.length;

    return {
      sudirmanSales,
      sudirmanCount,
      kelakapSales,
      kelakapCount,
      totalSales: sudirmanSales + kelakapSales,
      totalCount: sudirmanCount + kelakapCount,
    };
  };

  // Section 1 — Hari Ini
  const todayMetrics = useMemo(() => {
    return calculateOutletMetricsForPeriod(orders, (d, now) => {
      return (
        d.getDate() === now.getDate() &&
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear()
      );
    });
  }, [orders]);

  // Section 2 — 7 Hari Terakhir
  const last7DaysMetrics = useMemo(() => {
    return calculateOutletMetricsForPeriod(orders, (d, now) => {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setHours(0, 0, 0, 0);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      return d >= sevenDaysAgo;
    });
  }, [orders]);

  // Section 3 — 30 Hari Terakhir
  const last30DaysMetrics = useMemo(() => {
    return calculateOutletMetricsForPeriod(orders, (d, now) => {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setHours(0, 0, 0, 0);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
      return d >= thirtyDaysAgo;
    });
  }, [orders]);

  // Section 4 — Semua Waktu
  const allTimeMetrics = useMemo(() => {
    return calculateOutletMetricsForPeriod(orders, () => true);
  }, [orders]);

  // Daily Breakdown for 7 Days Trend Chart
  const last7DaysChartData = useMemo(() => {
    const now = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const targetDate = new Date(now);
      targetDate.setDate(now.getDate() - i);
      
      const dayLabel = targetDate.toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric' });
      
      const dayOrders = orders.filter((o) => {
        if (o.paymentStatus !== 'PAID') return false;
        const d = new Date(o.createdAt);
        return (
          d.getDate() === targetDate.getDate() &&
          d.getMonth() === targetDate.getMonth() &&
          d.getFullYear() === targetDate.getFullYear()
        );
      });

      const sudirman = dayOrders
        .filter((o) => matchesOutlet(o.outletId, 'outlet-sudirman'))
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

      const kelakap = dayOrders
        .filter((o) => matchesOutlet(o.outletId, 'outlet-kelakap'))
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

      days.push({
        label: dayLabel,
        sudirman,
        kelakap,
        total: sudirman + kelakap,
      });
    }
    return days;
  }, [orders]);

  // 30 Days Trend Data (6 Intervals of 5 Days)
  const last30DaysChartData = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 5; i >= 0; i--) {
      const endDate = new Date(now);
      endDate.setDate(now.getDate() - i * 5);
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 4);
      startDate.setHours(0, 0, 0, 0);

      const label = `${startDate.getDate()}/${startDate.getMonth() + 1} - ${endDate.getDate()}/${endDate.getMonth() + 1}`;

      const intervalOrders = orders.filter((o) => {
        if (o.paymentStatus !== 'PAID') return false;
        const d = new Date(o.createdAt);
        return d >= startDate && d <= endDate;
      });

      const sudirman = intervalOrders
        .filter((o) => matchesOutlet(o.outletId, 'outlet-sudirman'))
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

      const kelakap = intervalOrders
        .filter((o) => matchesOutlet(o.outletId, 'outlet-kelakap'))
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

      buckets.push({
        label,
        sudirman,
        kelakap,
        total: sudirman + kelakap,
      });
    }
    return buckets;
  }, [orders]);

  // Dynamic filter function for the audit table & summary bar
  const filterByDate = (order: CustomerOrder, filterPeriod: PeriodFilter): boolean => {
    if (filterPeriod === 'all') return true;
    const orderDate = new Date(order.createdAt);
    const now = new Date();

    if (filterPeriod === 'today') {
      return (
        orderDate.getDate() === now.getDate() &&
        orderDate.getMonth() === now.getMonth() &&
        orderDate.getFullYear() === now.getFullYear()
      );
    }

    if (filterPeriod === '7days') {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setHours(0, 0, 0, 0);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      return orderDate >= sevenDaysAgo;
    }

    if (filterPeriod === '30days') {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setHours(0, 0, 0, 0);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
      return orderDate >= thirtyDaysAgo;
    }

    if (filterPeriod === 'this_month') {
      return (
        orderDate.getMonth() === now.getMonth() &&
        orderDate.getFullYear() === now.getFullYear()
      );
    }

    return true;
  };

  // Filtered orders list for the Audit Table
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (!filterByDate(order, period)) return false;

      if (selectedOutlet !== 'ALL') {
        const matches = matchesOutlet(order.outletId, selectedOutlet);
        if (!matches) return false;
      }

      if (selectedPaymentStatus !== 'ALL') {
        if (order.paymentStatus !== selectedPaymentStatus) return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesQuery =
          order.orderNumber.toLowerCase().includes(q) ||
          order.customerName.toLowerCase().includes(q) ||
          (order.customerPhone && order.customerPhone.toLowerCase().includes(q)) ||
          order.outletName.toLowerCase().includes(q) ||
          (order.tableNumber && order.tableNumber.toLowerCase().includes(q));
        if (!matchesQuery) return false;
      }

      return true;
    });
  }, [orders, period, selectedOutlet, selectedPaymentStatus, searchQuery]);

  // Payment status stats summary
  const tableStats = useMemo(() => {
    const paidOrders = filteredOrders.filter((o) => o.paymentStatus === 'PAID');
    const waitingVerifOrders = filteredOrders.filter((o) => o.paymentStatus === 'WAITING VERIFICATION');
    const rejectedOrders = filteredOrders.filter((o) => o.paymentStatus === 'REJECTED');

    return {
      paidCount: paidOrders.length,
      waitingVerifCount: waitingVerifOrders.length,
      rejectedCount: rejectedOrders.length,
      totalSales: paidOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0),
    };
  }, [filteredOrders]);

  // Export to CSV
  const handleExportCSV = () => {
    if (filteredOrders.length === 0) {
      alert('Tidak ada data pesanan untuk diexport.');
      return;
    }

    const headers = [
      'No Order',
      'Waktu',
      'Outlet',
      'Customer',
      'No Telp',
      'Tipe Pesanan',
      'Meja',
      'Metode Bayar',
      'Status Pembayaran',
      'Status Pesanan',
      'Total (IDR)',
      'Item Pesanan',
    ];

    const rows = filteredOrders.map((o) => {
      const itemsText = o.items.map((it) => `${it.name} (${it.quantity}x)`).join('; ');
      return [
        `"${o.orderNumber}"`,
        `"${new Date(o.createdAt).toLocaleString('id-ID')}"`,
        `"${o.outletName}"`,
        `"${o.customerName}"`,
        `"${o.customerPhone || '-'}"`,
        `"${o.orderType}"`,
        `"${o.tableNumber || '-'}"`,
        `"${o.paymentMethod}"`,
        `"${o.paymentStatus}"`,
        `"${o.orderStatus}"`,
        o.totalAmount,
        `"${itemsText}"`,
      ].join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Leton_Sales_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div id="sales-report-manager" className="space-y-8 pb-16">
      {/* UI HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-[#E0F2FE] pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[#E0F2FE] text-[#0284C7] border border-[#BAE6FD] tracking-wider">
              SUPER ADMIN • LAPORAN PENJUALAN
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Live Supabase Sync
            </span>
          </div>
          <h1 className="text-2xl font-black tracking-tight text-[#172033] mt-2 font-display">
            Laporan Penjualan
          </h1>
          <p className="text-xs text-[#64748B] mt-0.5">
            Pantau penjualan dan jumlah transaksi setiap outlet berdasarkan periode waktu.
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            id="refresh-sales-btn"
            onClick={loadOrders}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-[#E0F2FE] text-xs font-semibold text-[#172033] hover:bg-[#F0F7FF] transition shadow-xs cursor-pointer"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin text-[#0284C7]' : 'text-[#0284C7]'} />
            Muat Ulang
          </button>
          <button
            id="export-csv-btn"
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#0284C7] hover:bg-[#0369A1] text-xs font-bold text-white transition shadow-xs cursor-pointer"
          >
            <Download size={14} />
            Export CSV
          </button>
          <button
            id="print-report-btn"
            onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-[#E0F2FE] text-xs font-semibold text-[#172033] hover:bg-[#F0F7FF] transition shadow-xs cursor-pointer"
          >
            <Printer size={14} className="text-[#0284C7]" />
            Cetak
          </button>
        </div>
      </div>

      {/* SECTION 1 — HARI INI */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between border-b border-[#E0F2FE] pb-2">
          <div>
            <h2 className="text-sm font-black tracking-wide text-[#172033] uppercase">HARI INI</h2>
            <p className="text-xs text-[#64748B]">Penjualan tanggal hari ini</p>
          </div>
          <span className="text-xs font-mono font-bold text-[#0284C7]">
            Total: Rp {todayMetrics.totalSales.toLocaleString('id-ID')}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card 1: LETON SUDIRMAN */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs relative overflow-hidden flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-[#172033] tracking-wide block">LETON SUDIRMAN</span>
                <span className="text-[11px] text-[#64748B]">Chapter 5 • Urban Hub</span>
              </div>
              <div className="w-8 h-8 rounded-xl bg-[#E0F2FE] flex items-center justify-center text-[#0284C7] shrink-0">
                <Store size={16} />
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-[#F0F7FF] flex items-baseline justify-between">
              <div className="text-2xl font-black text-[#172033] font-mono tabular-nums">
                Rp {todayMetrics.sudirmanSales.toLocaleString('id-ID')}
              </div>
              <div className="text-xs font-bold text-[#0284C7]">
                {todayMetrics.sudirmanCount} order
              </div>
            </div>
          </div>

          {/* Card 2: LETON KELAKAP 7 */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs relative overflow-hidden flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-[#172033] tracking-wide block">LETON KELAKAP 7</span>
                <span className="text-[11px] text-[#64748B]">Chapter 6 • Open Air</span>
              </div>
              <div className="w-8 h-8 rounded-xl bg-[#E0F2FE] flex items-center justify-center text-[#0284C7] shrink-0">
                <Store size={16} />
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-[#F0F7FF] flex items-baseline justify-between">
              <div className="text-2xl font-black text-[#172033] font-mono tabular-nums">
                Rp {todayMetrics.kelakapSales.toLocaleString('id-ID')}
              </div>
              <div className="text-xs font-bold text-[#0284C7]">
                {todayMetrics.kelakapCount} order
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — 7 HARI TERAKHIR */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between border-b border-[#E0F2FE] pb-2">
          <div>
            <h2 className="text-sm font-black tracking-wide text-[#172033] uppercase">7 HARI TERAKHIR</h2>
            <p className="text-xs text-[#64748B]">Ringkasan penjualan selama 7 hari terakhir</p>
          </div>
          <span className="text-xs font-mono font-bold text-[#0284C7]">
            Total: Rp {last7DaysMetrics.totalSales.toLocaleString('id-ID')}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card Sudirman */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[#172033]">LETON SUDIRMAN</span>
              <span className="text-xs font-bold text-[#0284C7]">{last7DaysMetrics.sudirmanCount} order</span>
            </div>
            <div className="text-2xl font-black text-[#172033] font-mono tabular-nums mt-1">
              Rp {last7DaysMetrics.sudirmanSales.toLocaleString('id-ID')}
            </div>
          </div>

          {/* Card Kelakap 7 */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[#172033]">LETON KELAKAP 7</span>
              <span className="text-xs font-bold text-[#0284C7]">{last7DaysMetrics.kelakapCount} order</span>
            </div>
            <div className="text-2xl font-black text-[#172033] font-mono tabular-nums mt-1">
              Rp {last7DaysMetrics.kelakapSales.toLocaleString('id-ID')}
            </div>
          </div>
        </div>

        {/* Mini Trend Chart 7 Hari */}
        <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-[#172033] flex items-center gap-1.5">
              <BarChart2 size={14} className="text-[#0284C7]" />
              Tren Penjualan Harian (7 Hari Terakhir)
            </span>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#0284C7]"></span>
                Sudirman
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#38BDF8]"></span>
                Kelakap 7
              </span>
            </div>
          </div>

          {/* Bar Visualization */}
          <div className="grid grid-cols-7 gap-2 pt-2 items-end h-32 border-b border-[#F0F7FF] pb-2">
            {last7DaysChartData.map((d, idx) => {
              const maxVal = Math.max(...last7DaysChartData.map((item) => item.total), 1);
              const sudirmanPct = Math.round((d.sudirman / maxVal) * 100);
              const kelakapPct = Math.round((d.kelakap / maxVal) * 100);

              return (
                <div key={idx} className="flex flex-col items-center gap-1.5 h-full justify-end group relative">
                  {/* Tooltip */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-10 bg-[#172033] text-white text-[10px] py-1 px-2 rounded shadow-md pointer-events-none whitespace-nowrap z-10">
                    <div>Sudirman: Rp {d.sudirman.toLocaleString('id-ID')}</div>
                    <div>Kelakap 7: Rp {d.kelakap.toLocaleString('id-ID')}</div>
                  </div>

                  <div className="w-full flex items-end justify-center gap-1 h-full">
                    {/* Sudirman Bar */}
                    <div
                      style={{ height: `${Math.max(sudirmanPct, 4)}%` }}
                      className="w-1/2 bg-[#0284C7] rounded-t-xs transition-all duration-300 hover:bg-[#0369A1]"
                    ></div>
                    {/* Kelakap Bar */}
                    <div
                      style={{ height: `${Math.max(kelakapPct, 4)}%` }}
                      className="w-1/2 bg-[#38BDF8] rounded-t-xs transition-all duration-300 hover:bg-[#0284C7]"
                    ></div>
                  </div>
                  <span className="text-[10px] text-[#64748B] font-mono truncate w-full text-center">
                    {d.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — 30 HARI TERAKHIR */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between border-b border-[#E0F2FE] pb-2">
          <div>
            <h2 className="text-sm font-black tracking-wide text-[#172033] uppercase">30 HARI TERAKHIR</h2>
            <p className="text-xs text-[#64748B]">Ringkasan penjualan selama 30 hari terakhir</p>
          </div>
          <span className="text-xs font-mono font-bold text-[#0284C7]">
            Total: Rp {last30DaysMetrics.totalSales.toLocaleString('id-ID')}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card Sudirman */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[#172033]">LETON SUDIRMAN</span>
              <span className="text-xs font-bold text-[#0284C7]">{last30DaysMetrics.sudirmanCount} order</span>
            </div>
            <div className="text-2xl font-black text-[#172033] font-mono tabular-nums mt-1">
              Rp {last30DaysMetrics.sudirmanSales.toLocaleString('id-ID')}
            </div>
          </div>

          {/* Card Kelakap 7 */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[#172033]">LETON KELAKAP 7</span>
              <span className="text-xs font-bold text-[#0284C7]">{last30DaysMetrics.kelakapCount} order</span>
            </div>
            <div className="text-2xl font-black text-[#172033] font-mono tabular-nums mt-1">
              Rp {last30DaysMetrics.kelakapSales.toLocaleString('id-ID')}
            </div>
          </div>
        </div>

        {/* Mini Trend Chart 30 Hari */}
        <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-[#172033] flex items-center gap-1.5">
              <TrendingUp size={14} className="text-[#0284C7]" />
              Tren Penjualan 30 Hari Terakhir (Interval 5 Harian)
            </span>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#0284C7]"></span>
                Sudirman
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#38BDF8]"></span>
                Kelakap 7
              </span>
            </div>
          </div>

          {/* Bar Interval Visualization */}
          <div className="grid grid-cols-6 gap-2 pt-2 items-end h-32 border-b border-[#F0F7FF] pb-2">
            {last30DaysChartData.map((d, idx) => {
              const maxVal = Math.max(...last30DaysChartData.map((item) => item.total), 1);
              const sudirmanPct = Math.round((d.sudirman / maxVal) * 100);
              const kelakapPct = Math.round((d.kelakap / maxVal) * 100);

              return (
                <div key={idx} className="flex flex-col items-center gap-1.5 h-full justify-end group relative">
                  {/* Tooltip */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-10 bg-[#172033] text-white text-[10px] py-1 px-2 rounded shadow-md pointer-events-none whitespace-nowrap z-10">
                    <div>Sudirman: Rp {d.sudirman.toLocaleString('id-ID')}</div>
                    <div>Kelakap 7: Rp {d.kelakap.toLocaleString('id-ID')}</div>
                  </div>

                  <div className="w-full flex items-end justify-center gap-1 h-full">
                    <div
                      style={{ height: `${Math.max(sudirmanPct, 4)}%` }}
                      className="w-1/2 bg-[#0284C7] rounded-t-xs transition-all duration-300 hover:bg-[#0369A1]"
                    ></div>
                    <div
                      style={{ height: `${Math.max(kelakapPct, 4)}%` }}
                      className="w-1/2 bg-[#38BDF8] rounded-t-xs transition-all duration-300 hover:bg-[#0284C7]"
                    ></div>
                  </div>
                  <span className="text-[10px] text-[#64748B] font-mono truncate w-full text-center">
                    {d.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — SEMUA WAKTU */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between border-b border-[#E0F2FE] pb-2">
          <div>
            <h2 className="text-sm font-black tracking-wide text-[#172033] uppercase">SEMUA WAKTU</h2>
            <p className="text-xs text-[#64748B]">Kumulatif seluruh data historis penjualan</p>
          </div>
          <span className="text-xs font-mono font-bold text-[#0284C7]">
            Total: Rp {allTimeMetrics.totalSales.toLocaleString('id-ID')}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card Sudirman */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[#172033]">LETON SUDIRMAN</span>
              <span className="text-xs font-bold text-[#0284C7]">{allTimeMetrics.sudirmanCount} total order</span>
            </div>
            <div className="text-2xl font-black text-[#172033] font-mono tabular-nums mt-1">
              Rp {allTimeMetrics.sudirmanSales.toLocaleString('id-ID')}
            </div>
          </div>

          {/* Card Kelakap 7 */}
          <div className="bg-white border border-[#E0F2FE] p-4 rounded-2xl shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-[#172033]">LETON KELAKAP 7</span>
              <span className="text-xs font-bold text-[#0284C7]">{allTimeMetrics.kelakapCount} total order</span>
            </div>
            <div className="text-2xl font-black text-[#172033] font-mono tabular-nums mt-1">
              Rp {allTimeMetrics.kelakapSales.toLocaleString('id-ID')}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — PERBANDINGAN OUTLET (COMPARISON) */}
      <section className="space-y-3 bg-white border border-[#E0F2FE] p-5 rounded-2xl shadow-xs">
        <div className="border-b border-[#E0F2FE] pb-2">
          <h2 className="text-sm font-black tracking-wide text-[#172033] uppercase">PERBANDINGAN OUTLET</h2>
          <p className="text-xs text-[#64748B]">Perbandingan objektif kontribusi penjualan antar outlet</p>
        </div>

        {(() => {
          const grandTotal = allTimeMetrics.totalSales || 1;
          const sudirmanPct = Math.round((allTimeMetrics.sudirmanSales / grandTotal) * 100);
          const kelakapPct = Math.round((allTimeMetrics.kelakapSales / grandTotal) * 100);

          return (
            <div className="space-y-4 pt-2">
              {/* Horizontal Bar Visual */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-bold text-[#172033]">
                  <span>Distribusi Penjualan Kumulatif</span>
                  <span className="font-mono text-[#0284C7]">Rp {allTimeMetrics.totalSales.toLocaleString('id-ID')}</span>
                </div>
                <div className="h-4 w-full bg-[#F0F7FF] rounded-full overflow-hidden flex border border-[#E0F2FE]">
                  <div
                    style={{ width: `${sudirmanPct}%` }}
                    className="bg-[#0284C7] h-full transition-all duration-500"
                    title={`Sudirman: ${sudirmanPct}%`}
                  ></div>
                  <div
                    style={{ width: `${kelakapPct}%` }}
                    className="bg-[#38BDF8] h-full transition-all duration-500"
                    title={`Kelakap 7: ${kelakapPct}%`}
                  ></div>
                </div>
              </div>

              {/* Objective Metrics Comparison Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="p-3.5 rounded-xl border border-[#E0F2FE] bg-[#F8FBFF] flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-[#172033]">LETON SUDIRMAN</div>
                    <div className="text-lg font-black text-[#0284C7] font-mono tabular-nums mt-0.5">
                      Rp {allTimeMetrics.sudirmanSales.toLocaleString('id-ID')}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-[#172033]">{allTimeMetrics.sudirmanCount} order</span>
                    <div className="text-[11px] text-[#64748B] font-mono">{sudirmanPct}% dari total</div>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl border border-[#E0F2FE] bg-[#F8FBFF] flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-[#172033]">LETON KELAKAP 7</div>
                    <div className="text-lg font-black text-[#0284C7] font-mono tabular-nums mt-0.5">
                      Rp {allTimeMetrics.kelakapSales.toLocaleString('id-ID')}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-[#172033]">{allTimeMetrics.kelakapCount} order</span>
                    <div className="text-[11px] text-[#64748B] font-mono">{kelakapPct}% dari total</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </section>

      {/* SECTION 6 — AUDIT TABEL & FILTER PERIODE */}
      <section className="space-y-4 pt-4 border-t border-[#E0F2FE]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-sm font-black tracking-wide text-[#172033] uppercase">RIWAYAT TRANSAKSI DETIL</h2>
            <p className="text-xs text-[#64748B]">Audit transaksi dan verifikasi bukti pembayaran</p>
          </div>

          {/* Period Filter Tabs */}
          <div className="flex items-center gap-1.5 bg-white p-1 rounded-xl border border-[#E0F2FE] overflow-x-auto shadow-xs">
            {[
              { id: 'all', label: 'Semua' },
              { id: 'today', label: 'Hari Ini' },
              { id: '7days', label: '7 Hari' },
              { id: '30days', label: '30 Hari' },
              { id: 'this_month', label: 'Bulan Ini' },
            ].map((tab) => (
              <button
                key={tab.id}
                id={`period-tab-${tab.id}`}
                onClick={() => setPeriod(tab.id as PeriodFilter)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap cursor-pointer ${
                  period === tab.id
                    ? 'bg-[#0284C7] text-white shadow-xs'
                    : 'text-[#64748B] hover:text-[#172033] hover:bg-[#F0F7FF]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Status Summary Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white p-3.5 rounded-2xl border border-[#E0F2FE] shadow-xs">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
              <CheckCircle2 size={16} />
            </div>
            <div>
              <div className="text-[11px] text-[#64748B]">Lunas / Terverifikasi</div>
              <div className="text-sm font-bold text-emerald-700">{tableStats.paidCount} Transaksi</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
              <Clock size={16} />
            </div>
            <div>
              <div className="text-[11px] text-[#64748B]">Menunggu QRIS</div>
              <div className="text-sm font-bold text-amber-700">{tableStats.waitingVerifCount} Transaksi</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600 shrink-0">
              <XCircle size={16} />
            </div>
            <div>
              <div className="text-[11px] text-[#64748B]">Ditolak / Batal</div>
              <div className="text-sm font-bold text-rose-700">{tableStats.rejectedCount} Transaksi</div>
            </div>
          </div>
        </div>

        {/* Filter Controls & Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white p-3 rounded-2xl border border-[#E0F2FE] shadow-xs">
          <div className="flex items-center gap-2 flex-1 flex-wrap sm:flex-nowrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                type="text"
                placeholder="Cari order #, nama pelanggan, whatsapp..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#F8FBFF] border border-[#E0F2FE] rounded-xl pl-9 pr-3 py-2 text-xs text-[#172033] placeholder-[#94A3B8] focus:outline-none focus:border-[#38BDF8]"
              />
            </div>

            {/* Outlet Filter Dropdown */}
            <select
              value={selectedOutlet}
              onChange={(e) => setSelectedOutlet(e.target.value)}
              className="bg-[#F8FBFF] border border-[#E0F2FE] text-xs text-[#172033] rounded-xl px-3 py-2 focus:outline-none focus:border-[#38BDF8]"
            >
              <option value="ALL">Semua Outlet</option>
              <option value="outlet-sudirman">Sudirman Hub (Ch. 5)</option>
              <option value="outlet-kelakap">Kelakap 7 (Ch. 6)</option>
            </select>

            {/* Payment Status Dropdown */}
            <select
              value={selectedPaymentStatus}
              onChange={(e) => setSelectedPaymentStatus(e.target.value)}
              className="bg-[#F8FBFF] border border-[#E0F2FE] text-xs text-[#172033] rounded-xl px-3 py-2 focus:outline-none focus:border-[#38BDF8]"
            >
              <option value="ALL">Semua Status Bayar</option>
              <option value="PAID">Lunas (PAID)</option>
              <option value="WAITING VERIFICATION">Menunggu Verifikasi</option>
              <option value="REJECTED">Ditolak</option>
            </select>
          </div>

          <div className="text-xs text-[#64748B] font-mono shrink-0">
            Menampilkan <strong className="text-[#172033]">{filteredOrders.length}</strong> pesanan
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E0F2FE] rounded-2xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#F8FBFF] text-[#64748B] uppercase font-mono text-[10px] border-b border-[#E0F2FE]">
                <tr>
                  <th className="px-4 py-3">No. Order</th>
                  <th className="px-4 py-3">Waktu</th>
                  <th className="px-4 py-3">Outlet</th>
                  <th className="px-4 py-3">Pelanggan</th>
                  <th className="px-4 py-3">Layanan</th>
                  <th className="px-4 py-3">Metode Bayar</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-center">Bukti QRIS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0F2FE] text-[#172033]">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-[#64748B]">
                      Tidak ada transaksi ditemukan pada filter ini.
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((o) => (
                    <tr key={o.id} className="hover:bg-[#F8FBFF] transition-colors">
                      <td className="px-4 py-3 font-mono font-bold text-[#0284C7]">
                        {o.orderNumber}
                      </td>
                      <td className="px-4 py-3 text-[#64748B]">
                        {new Date(o.createdAt).toLocaleString('id-ID', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 font-medium">{o.outletName}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{o.customerName}</div>
                        {o.customerPhone && (
                          <div className="text-[10px] text-[#64748B]">{o.customerPhone}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full bg-[#F0F7FF] text-[#0284C7] font-semibold text-[10px]">
                          {o.orderType} {o.tableNumber ? `(Meja ${o.tableNumber})` : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px]">{o.paymentMethod}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            o.paymentStatus === 'PAID'
                              ? 'bg-emerald-50 text-emerald-700'
                              : o.paymentStatus === 'WAITING VERIFICATION'
                              ? 'bg-amber-50 text-amber-700'
                              : o.paymentStatus === 'REJECTED'
                              ? 'bg-rose-50 text-rose-700'
                              : 'bg-[#F0F7FF] text-[#0284C7]'
                          }`}
                        >
                          {o.paymentStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-[#172033]">
                        Rp {o.totalAmount.toLocaleString('id-ID')}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {o.paymentReceiptUrl ? (
                          <button
                            onClick={() => setPreviewReceiptUrl(o.paymentReceiptUrl || null)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#E0F2FE] text-[#0284C7] hover:bg-[#BAE6FD] text-[10px] font-bold transition-colors cursor-pointer"
                          >
                            <Eye size={12} />
                            <span>Lihat</span>
                          </button>
                        ) : (
                          <span className="text-[#94A3B8] text-[10px]">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Receipt Modal Preview */}
      {previewReceiptUrl && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E0F2FE] p-6 max-w-lg w-full shadow-2xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-[#E0F2FE]">
              <span className="font-bold text-sm text-[#172033]">Bukti Transfer QRIS</span>
              <button
                onClick={() => setPreviewReceiptUrl(null)}
                className="text-[#64748B] hover:text-[#172033] font-bold text-sm"
              >
                ✕
              </button>
            </div>
            <div className="py-4 flex justify-center">
              <img
                src={previewReceiptUrl}
                alt="Receipt Preview"
                className="max-h-[60vh] object-contain rounded-xl border border-[#E0F2FE]"
              />
            </div>
            <div className="pt-3 border-t border-[#E0F2FE] text-right">
              <button
                onClick={() => setPreviewReceiptUrl(null)}
                className="px-4 py-2 rounded-xl bg-[#0284C7] text-white text-xs font-bold cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
